import { useQuery } from '@tanstack/react-query';
import { create } from 'zustand';

import {
  deleteServiceRolePermissions,
  getBasePermissions,
  getSeatPermissions,
  getServicePlanSeat,
  getServiceRolePermissions,
  listServicePeople,
  updatePersonPermissions,
  upsertServiceRolePermissions,
} from './permissions.api';
import type {
  DeleteServiceRolePermissionsPayload,
  ListServicePeopleParams,
  RolePermissions,
  SeatPermissions,
  ServicePerson,
  UpdatePersonPermissionsPayload,
  UpsertServiceRolePermissionsPayload,
} from './permissions.dto';

type PermissionsState = {
  serviceRolePermissions: RolePermissions[];
  basePermissions: string[];
  seatPermissions: SeatPermissions[];
  /**
   * Whether `GET /permissions/seats` has answered — either way round.
   *
   * An empty `seatPermissions` means two different things, and callers have to tell them
   * apart: nothing asked for yet, or asked and refused. Plan gating reads `hidden` for
   * every withheld permission until the lists land, so a route guard acting on that answer
   * too early bounces the user off a page it would have explained a moment later.
   */
  seatPermissionsSettled: boolean;
  servicePeople: ServicePerson[];

  fetchServiceRolePermissions: (serviceId: string) => Promise<RolePermissions[]>;
  fetchBasePermissions: () => Promise<string[]>;
  fetchSeatPermissions: () => Promise<SeatPermissions[]>;
  upsertServiceRolePermissions: (
    serviceId: string,
    payload: UpsertServiceRolePermissionsPayload,
  ) => Promise<void>;
  deleteServiceRolePermissions: (
    serviceId: string,
    payload: DeleteServiceRolePermissionsPayload,
  ) => Promise<void>;
  fetchServicePeople: (
    serviceId: string,
    params?: ListServicePeopleParams,
  ) => Promise<ServicePerson[]>;
  updatePersonPermissions: (payload: UpdatePersonPermissionsPayload) => Promise<void>;
};

export const usePermissions = create<PermissionsState>((set) => ({
  serviceRolePermissions: [],
  basePermissions: [],
  seatPermissions: [],
  seatPermissionsSettled: false,
  servicePeople: [],

  fetchServiceRolePermissions: async (serviceId) => {
    const data = await getServiceRolePermissions(serviceId);
    set({ serviceRolePermissions: data });
    return data;
  },

  fetchBasePermissions: async () => {
    const data = await getBasePermissions();
    set({ basePermissions: data });
    return data;
  },

  fetchSeatPermissions: async () => {
    try {
      const data = await getSeatPermissions();
      set({ seatPermissions: data, seatPermissionsSettled: true });
      return data;
    } catch (error) {
      // A refusal settles the question too: with no allow-lists nothing can be attributed
      // to the plan, and callers need to know that is the final answer rather than wait.
      set({ seatPermissionsSettled: true });
      throw error;
    }
  },

  upsertServiceRolePermissions: async (serviceId, payload) => {
    await upsertServiceRolePermissions(serviceId, payload);
  },

  deleteServiceRolePermissions: async (serviceId, payload) => {
    await deleteServiceRolePermissions(serviceId, payload);
  },

  fetchServicePeople: async (serviceId, params) => {
    const page = await listServicePeople(serviceId, params);
    const items = page.items ?? [];
    set({ servicePeople: items });
    return items;
  },

  updatePersonPermissions: async (payload) => {
    await updatePersonPermissions(payload);
  },
}));

/** The seat one service's plan pays for; see `getServicePlanSeat`. */
export const usePlanSeatQuery = (serviceId: string) =>
  useQuery({
    queryKey: ['permissions', 'plan-seat', serviceId],
    queryFn: () => getServicePlanSeat(serviceId),
    enabled: !!serviceId,
  });
